import { Alpine } from '../core/alpine-core.js';
import { apiFetch } from './api.js';

function applyEffectiveFromAccountResponse(data) {
  try {
    Alpine.store('features')?.applyServerCapabilities?.(data?.effectiveCapabilities);
    Alpine.store('subscriptionGate')?.setFromAccountSettings?.(data);
  } catch {
    /* ignore */
  }
}

function venueStaffRoleRank(role) {
  const rk = { player: 1, viewer: 1, manager: 2, admin: 3, owner: 4 };
  return rk[String(role || '').toLowerCase()] || 0;
}

Alpine.data('welcomeWizard', () => ({
  loading: true,
  err: '',
  /** 0 intro — 1 subscription — 2 demo — 3 invites — 4 finish */
  step: 0,

  accountId: '',
  onboarding: null,
  hints: { subscriptionRequired: true },
  stepLocked: {},

  subscriptionKeyInput: '',
  activatingKey: false,
  subscriptionSkipAcknowledged: false,

  wantDemoImport: false,
  demoModules: {
    venues: true,
    leagueRules: true,
    teams: true,
    players: true,
    fixtures: true,
  },
  demoClearExisting: false,
  demoImporting: false,
  demoImportRanSuccess: false,

  inviteRows: [{ email: '', role: 'manager' }],
  inviteBatchDone: false,
  inviteBatchSummary: null,

  finishSaving: false,

  venueStaffRoleRank,
  hasAtLeastVenueRole(min) {
    const need = venueStaffRoleRank(min);
    const got = venueStaffRoleRank(this.$store?.session?.me?.role);
    return need > 0 && got >= need;
  },

  canManageSubscriptionKey() {
    return this.hasAtLeastVenueRole('admin');
  },

  canRunDemoImport() {
    return this.hasAtLeastVenueRole('admin');
  },

  canSendStaffInvites() {
    const r = String(this.$store?.session?.me?.role || '').toLowerCase();
    return r === 'owner' || r === 'admin';
  },

  subscriptionSatisfied() {
    return !this.hints?.subscriptionRequired;
  },

  demoModuleEntries() {
    return [
      { key: 'venues', label: 'Venues' },
      { key: 'leagueRules', label: 'League rules' },
      { key: 'teams', label: 'Teams' },
      { key: 'players', label: 'Players' },
      { key: 'fixtures', label: 'Fixtures' },
    ];
  },

  inviteRoleSelectOptions() {
    const r = String(this.$store?.session?.me?.role || '').toLowerCase();
    const max = venueStaffRoleRank(r);
    const order = ['viewer', 'manager', 'admin'];
    if (r === 'owner') order.push('owner');
    const labels = {
      owner: 'Owner',
      admin: 'Admin',
      manager: 'Manager',
      viewer: 'Viewer',
    };
    return order
      .filter((id) => venueStaffRoleRank(id) <= max)
      .map((id) => ({ id, label: labels[id] || id }));
  },

  stepLabels() {
    return ['Welcome', 'Subscription', 'Demo data', 'Team invites', 'Done'];
  },

  async init() {
    const r = String(this.$store?.session?.me?.role || '').toLowerCase();
    if (r === 'player') {
      const href = this.$store?.session?.playersSelfAdminHref || '/admin/dashboard';
      globalThis.location.replace(href);
      return;
    }
    await this.loadOnboarding();
    this.loading = false;
  },

  async loadOnboarding() {
    this.err = '';
    try {
      const res = await apiFetch('/api/admin/onboarding');
      const j = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(j.message || j.error || 'Could not load onboarding');
      this.accountId = String(j.accountId || '').trim();
      this.onboarding = j.onboarding || {};
      this.hints = { subscriptionRequired: true, ...(j.hints || {}) };
      this.stepLocked = { ...(j.stepLocked || {}) };
      if (this.subscriptionSatisfied()) this.subscriptionSkipAcknowledged = true;
    } catch (e) {
      this.err = String(e.message || 'load_failed');
    }
  },

  async refreshHintsFromAccount() {
    try {
      await this.loadOnboarding();
    } catch {
      /* ignore */
    }
  },

  goBack() {
    if (this.step > 0) this.step -= 1;
    this.err = '';
  },

  async goNext() {
    this.err = '';
    if (this.step === 1) {
      const need = !!this.hints?.subscriptionRequired;
      if (need && !this.subscriptionSatisfied()) {
        if (this.canManageSubscriptionKey()) {
          const key = String(this.subscriptionKeyInput || '').trim();
          if (key) {
            await this.activateSubscriptionKey();
            if (!this.subscriptionSatisfied()) return;
          } else if (!this.subscriptionSkipAcknowledged) {
            this.err = 'Paste your subscription key, or confirm you’ll activate later below.';
            return;
          }
        }
      }
    }
    if (this.step === 2 && this.wantDemoImport && this.canRunDemoImport()) {
      await this.runDemoImport();
      if (this.err) return;
    }
    if (this.step === 3) {
      if (this.canSendStaffInvites()) {
        if (!this.inviteBatchDone) {
          await this.sendInviteBatch();
          if (this.err) return;
        }
      } else {
        this.inviteBatchDone = true;
        this.inviteBatchSummary = { skipped: true, reason: 'role' };
      }
    }
    if (this.step < 4) this.step += 1;
  },

  async activateSubscriptionKey() {
    if (this.activatingKey) return;
    this.activatingKey = true;
    this.err = '';
    try {
      const res = await apiFetch('/api/admin/account-settings/subscription-key', {
        method: 'PUT',
        body: { subscriptionKey: String(this.subscriptionKeyInput || '').trim() },
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.error || data?.message || 'Could not activate key');
      applyEffectiveFromAccountResponse(data);
      this.subscriptionKeyInput = '';
      await apiFetch('/api/admin/account-settings/refresh', { method: 'POST', body: {}, timeoutMs: 25000 }).catch(() => {});
      await this.loadOnboarding();
      this.subscriptionSkipAcknowledged = true;
      this.$store?.toastr?.success?.('Subscription key activated');
    } catch (e) {
      this.err = String(e.message || 'activation_failed');
    } finally {
      this.activatingKey = false;
    }
  },

  async runDemoImport() {
    if (this.demoImporting) return;
    const m = this.demoModules || {};
    if (!m.venues && !m.leagueRules && !m.teams && !m.players && !m.fixtures) {
      this.err = 'Choose at least one demo category, or turn off demo import.';
      return;
    }
    this.demoImporting = true;
    this.err = '';
    try {
      const res = await apiFetch('/admin/account-settings/demo-import', {
        method: 'POST',
        body: {
          clearExisting: !!this.demoClearExisting,
          importVenues: !!m.venues,
          importLeagueRules: !!m.leagueRules,
          importTeams: !!m.teams,
          importPlayers: !!m.players,
          importFixtures: !!m.fixtures,
        },
        timeoutMs: 10 * 60 * 1000,
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.error || data?.message || 'Demo import failed');
      await this.loadOnboarding();
      this.demoImportRanSuccess = true;
      this.$store?.toastr?.success?.('Demo data imported');
    } catch (e) {
      this.err = String(e.message || 'demo_import_failed');
    } finally {
      this.demoImporting = false;
    }
  },

  addInviteRow() {
    if (this.inviteRows.length >= 12) return;
    this.inviteRows.push({ email: '', role: 'manager' });
  },

  removeInviteRow(i) {
    if (this.inviteRows.length <= 1) return;
    this.inviteRows.splice(i, 1);
  },

  async sendInviteBatch() {
    const rows = (this.inviteRows || [])
      .map((r) => ({
        email: String(r.email || '').trim().toLowerCase(),
        role: String(r.role || 'manager').trim().toLowerCase(),
      }))
      .filter((r) => r.email && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(r.email));

    if (!rows.length) {
      this.inviteBatchDone = true;
      this.inviteBatchSummary = { sent: 0, skipped: true };
      return;
    }

    const sent = [];
    const failed = [];
    for (const row of rows) {
      try {
        const res = await apiFetch('/api/admin/auth/invite', {
          method: 'POST',
          body: { email: row.email, role: row.role },
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) {
          failed.push({ email: row.email, message: data?.message || data?.error || 'failed' });
        } else {
          sent.push(row.email);
        }
      } catch (e) {
        failed.push({ email: row.email, message: String(e.message || 'failed') });
      }
    }
    this.inviteBatchSummary = { sent: sent.length, sentList: sent, failed };
    if (failed.length) {
      this.inviteBatchDone = false;
      this.inviteRows = failed.map((f) => {
        const orig = rows.find((r) => r.email === f.email);
        return { email: f.email, role: orig?.role || 'manager' };
      });
      if (!this.inviteRows.length) this.inviteRows = [{ email: '', role: 'manager' }];
      this.err = failed.map((f) => `${f.email}: ${f.message}`).join(' · ');
      this.$store?.toastr?.error?.('Some invites failed — fix emails and try again');
    } else {
      this.inviteBatchDone = true;
      this.err = '';
      if (sent.length) this.$store?.toastr?.success?.(`${sent.length} invite(s) sent`);
    }
    await this.loadOnboarding();
  },

  async saveOnboardingPatch(patch) {
    const res = await apiFetch('/api/admin/onboarding', {
      method: 'POST',
      body: patch,
    });
    const j = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(j.message || j.error || 'save_failed');
    this.onboarding = j.onboarding || this.onboarding;
    this.hints = { ...this.hints, ...(j.hints || {}) };
    this.stepLocked = { ...this.stepLocked, ...(j.stepLocked || {}) };
  },

  async finishToDashboard() {
    this.finishSaving = true;
    this.err = '';
    try {
      await this.saveOnboardingPatch({
        complete: true,
        steps: {
          accountId: true,
          subscription: !!(this.stepLocked?.subscription || this.subscriptionSatisfied()),
          demoImport: !!(this.stepLocked?.demoImport || this.demoImportRanSuccess),
          inviteUsers: !!(
            this.stepLocked?.inviteUsers
            || (Number(this.inviteBatchSummary?.sent) > 0)
          ),
        },
      });
      globalThis.location.href = '/admin/dashboard';
    } catch (e) {
      this.err = String(e.message || 'finish_failed');
    } finally {
      this.finishSaving = false;
    }
  },

  async dismissToDashboard() {
    try {
      await apiFetch('/api/admin/onboarding', {
        method: 'POST',
        body: { dismiss: true },
      });
    } catch {
      /* ignore */
    }
    globalThis.location.href = '/admin/dashboard';
  },
}));
